import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography, Box, Paper, Button, Stack, Tabs, Tab, TextField, MenuItem, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, IconButton, Tooltip, Switch, FormControlLabel,
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { tokens } from '../../theme';
import { apiClient } from '../../services/api';
import { useSalonContextStore } from '../../store/salonContextStore';
import {
  giftCardApi, packageApi, GiftCard, ServicePackage, ClientPackage,
} from '../../services/giftService';
import {
  PageHeader, SectionCard, StatTile, EmptyState, LoadingState, StatusPill, useConfirm, PillTone,
} from '../../components/ui';
import { formatMoney, formatNumber, formatDate, countLabel } from '../../utils/format';

interface ClientOpt { id: string; firstName: string; lastName: string; }
interface ServiceOpt { id: string; name: string; price: number; }

const CARD_STATUS: Record<string, { label: string; tone: PillTone }> = {
  ACTIVE: { label: 'Aktywna', tone: 'success' },
  USED: { label: 'Wykorzystana', tone: 'neutral' },
  EXPIRED: { label: 'Wygasła', tone: 'warning' },
  CANCELLED: { label: 'Anulowana', tone: 'danger' },
};
const PKG_STATUS: Record<string, { label: string; tone: PillTone }> = {
  ACTIVE: { label: 'Aktywny', tone: 'success' },
  USED: { label: 'Wykorzystany', tone: 'neutral' },
  EXPIRED: { label: 'Wygasł', tone: 'warning' },
  CANCELLED: { label: 'Anulowany', tone: 'danger' },
};

const zl = (v: string | number | null | undefined) => formatMoney(v || 0);
const dt = (v?: string | null) => (v ? formatDate(v) : '—');
const unit = (label: string) => ({ endAdornment: <InputAdornment position="end">{label}</InputAdornment> });
const tabular = { fontVariantNumeric: 'tabular-nums' } as const;

export const SalonGiftsPage: React.FC = () => {
  const { activeSalonId } = useSalonContextStore();
  const confirm = useConfirm();
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [services, setServices] = useState<ServiceOpt[]>([]);

  // ── karty ──
  const [cards, setCards] = useState<GiftCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardSearch, setCardSearch] = useState('');
  const [cardStatus, setCardStatus] = useState('');
  const [cardDialog, setCardDialog] = useState(false);
  const [cardForm, setCardForm] = useState({ value: '', clientId: '', expiresAt: '', note: '' });
  const [cardSaving, setCardSaving] = useState(false);
  const [redeemCard, setRedeemCard] = useState<GiftCard | null>(null);
  const [redeemForm, setRedeemForm] = useState({ amount: '', note: '' });
  const [dialogErr, setDialogErr] = useState('');

  // ── pakiety ──
  const [pkgs, setPkgs] = useState<ServicePackage[]>([]);
  const [sold, setSold] = useState<ClientPackage[]>([]);
  const [pkgLoading, setPkgLoading] = useState(false);
  const [pkgDialog, setPkgDialog] = useState(false);
  const [pkgForm, setPkgForm] = useState({ name: '', description: '', price: '', originalPrice: '', validityDays: '' });
  const [pkgItems, setPkgItems] = useState<{ serviceId: string; quantity: number }[]>([{ serviceId: '', quantity: 1 }]);
  const [pkgSaving, setPkgSaving] = useState(false);
  const [sellPkg, setSellPkg] = useState<ServicePackage | null>(null);
  const [sellForm, setSellForm] = useState({ clientId: '', pricePaid: '' });
  const [usePkg, setUsePkg] = useState<ClientPackage | null>(null);
  const [useServiceId, setUseServiceId] = useState('');

  const err = (e: unknown, fallback: string) => {
    const d = (e as { response?: { data?: { error?: string; errors?: { msg?: string }[] } } }).response?.data;
    return d?.error || d?.errors?.[0]?.msg || fallback;
  };

  // ── dane pomocnicze ──
  useEffect(() => {
    if (!activeSalonId) return;
    // API przyjmuje limit 1–100; przy 200 zwracalo 400 i lista klientow w oknach byla pusta.
    apiClient.get(`/clients/salon/${activeSalonId}`, { params: { limit: 100 } })
      .then((r) => setClients(r.data.data || []))
      .catch(() => setClients([]));
    apiClient.get(`/services/salon/${activeSalonId}`)
      .then((r) => setServices((Array.isArray(r.data) ? r.data : r.data.data || []).map((s: any) => ({ id: s.id, name: s.name, price: Number(s.price) }))))
      .catch(() => setServices([]));
  }, [activeSalonId]);

  const loadCards = useCallback(async () => {
    if (!activeSalonId) return;
    setCardsLoading(true);
    try {
      setCards(await giftCardApi.list({ salonId: activeSalonId, ...(cardStatus ? { status: cardStatus } : {}), ...(cardSearch ? { search: cardSearch } : {}) }));
    } catch { setCards([]); } finally { setCardsLoading(false); }
  }, [activeSalonId, cardStatus, cardSearch]);
  useEffect(() => { loadCards(); }, [loadCards]);

  const loadPkgs = useCallback(async () => {
    if (!activeSalonId) return;
    setPkgLoading(true);
    try {
      const [p, s] = await Promise.all([
        packageApi.list(activeSalonId),
        packageApi.listSold({ salonId: activeSalonId }),
      ]);
      setPkgs(p); setSold(s);
    } catch { setPkgs([]); setSold([]); } finally { setPkgLoading(false); }
  }, [activeSalonId]);
  useEffect(() => { loadPkgs(); }, [loadPkgs]);

  // ── statystyki nagłówka ──
  const stats = useMemo(() => ({
    activeCards: cards.filter((c) => c.status === 'ACTIVE').length,
    cardBalance: cards.filter((c) => c.status === 'ACTIVE').reduce((s, c) => s + Number(c.balance), 0),
    cardSold: cards.reduce((s, c) => s + Number(c.initialValue), 0),
    activePkgs: pkgs.filter((p) => p.isActive).length,
    soldActive: sold.filter((s0) => s0.status === 'ACTIVE').length,
  }), [cards, pkgs, sold]);

  // ── akcje kart ──
  const createCard = async () => {
    if (!activeSalonId) return;
    const value = Number(cardForm.value);
    if (!(value > 0)) { setDialogErr('Podaj wartość karty.'); return; }
    setCardSaving(true); setDialogErr('');
    try {
      const created = await giftCardApi.create({
        salonId: activeSalonId,
        initialValue: value,
        ...(cardForm.clientId ? { clientId: cardForm.clientId } : {}),
        ...(cardForm.expiresAt ? { expiresAt: cardForm.expiresAt } : {}),
        ...(cardForm.note.trim() ? { note: cardForm.note.trim() } : {}),
      });
      setCardDialog(false);
      setCardForm({ value: '', clientId: '', expiresAt: '', note: '' });
      setToast({ msg: `Karta utworzona — kod: ${created.code}`, sev: 'success' });
      loadCards();
    } catch (e) { setDialogErr(err(e, 'Nie udało się utworzyć karty.')); }
    finally { setCardSaving(false); }
  };

  const doRedeem = async () => {
    if (!redeemCard) return;
    const amount = Number(redeemForm.amount);
    if (!(amount > 0)) { setDialogErr('Podaj kwotę realizacji.'); return; }
    setDialogErr('');
    try {
      await giftCardApi.redeem(redeemCard.id, amount, redeemForm.note.trim() || undefined);
      setRedeemCard(null);
      setRedeemForm({ amount: '', note: '' });
      setToast({ msg: 'Zrealizowano płatność kartą', sev: 'success' });
      loadCards();
    } catch (e) { setDialogErr(err(e, 'Nie udało się zrealizować karty.')); }
  };

  const cancelCard = async (card: GiftCard) => {
    const hasBalance = Number(card.balance) > 0;
    const ok = await confirm({
      title: 'Anulować kartę podarunkową?',
      details: (
        <>
          <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{card.code}</Box>
          {card.client ? ` · ${card.client.firstName} ${card.client.lastName}` : ''}
          <br />
          Saldo: <strong>{zl(card.balance)}</strong> z {zl(card.initialValue)}
        </>
      ),
      description: hasBalance
        ? `Saldo ${zl(card.balance)} przepadnie, a karty nie będzie można już zrealizować. Tej operacji nie można cofnąć.`
        : 'Karty nie będzie można już zrealizować. Tej operacji nie można cofnąć.',
      confirmLabel: 'Anuluj kartę',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await giftCardApi.cancel(card.id);
      setToast({ msg: 'Karta anulowana', sev: 'success' });
      loadCards();
    } catch (e) { setToast({ msg: err(e, 'Nie udało się anulować karty.'), sev: 'error' }); }
  };

  // ── akcje pakietów ──
  const createPkg = async () => {
    if (!activeSalonId) return;
    const items = pkgItems.filter((i) => i.serviceId);
    if (pkgForm.name.trim().length < 2) { setDialogErr('Podaj nazwę pakietu.'); return; }
    if (!(Number(pkgForm.price) > 0)) { setDialogErr('Podaj cenę pakietu.'); return; }
    if (items.length === 0) { setDialogErr('Dodaj co najmniej jedną usługę.'); return; }
    setPkgSaving(true); setDialogErr('');
    try {
      await packageApi.create({
        salonId: activeSalonId,
        name: pkgForm.name.trim(),
        ...(pkgForm.description.trim() ? { description: pkgForm.description.trim() } : {}),
        price: Number(pkgForm.price),
        ...(pkgForm.originalPrice ? { originalPrice: Number(pkgForm.originalPrice) } : {}),
        ...(pkgForm.validityDays ? { validityDays: Number(pkgForm.validityDays) } : {}),
        items,
      });
      setPkgDialog(false);
      setPkgForm({ name: '', description: '', price: '', originalPrice: '', validityDays: '' });
      setPkgItems([{ serviceId: '', quantity: 1 }]);
      setToast({ msg: 'Pakiet utworzony', sev: 'success' });
      loadPkgs();
    } catch (e) { setDialogErr(err(e, 'Nie udało się utworzyć pakietu.')); }
    finally { setPkgSaving(false); }
  };

  const togglePkg = async (p: ServicePackage) => {
    try {
      await packageApi.update(p.id, { isActive: !p.isActive });
      loadPkgs();
    } catch (e) { setToast({ msg: err(e, 'Nie udało się zmienić pakietu.'), sev: 'error' }); }
  };

  const doSell = async () => {
    if (!sellPkg) return;
    if (!sellForm.clientId) { setDialogErr('Wybierz klienta.'); return; }
    setDialogErr('');
    try {
      await packageApi.sell(sellPkg.id, {
        clientId: sellForm.clientId,
        ...(sellForm.pricePaid ? { pricePaid: Number(sellForm.pricePaid) } : {}),
      });
      setSellPkg(null);
      setSellForm({ clientId: '', pricePaid: '' });
      setToast({ msg: 'Pakiet sprzedany', sev: 'success' });
      loadPkgs();
    } catch (e) { setDialogErr(err(e, 'Nie udało się sprzedać pakietu.')); }
  };

  const doUse = async () => {
    if (!usePkg || !useServiceId) { setDialogErr('Wybierz usługę.'); return; }
    setDialogErr('');
    try {
      await packageApi.useSold(usePkg.id, useServiceId);
      setUsePkg(null);
      setUseServiceId('');
      setToast({ msg: 'Odnotowano wykorzystanie usługi z pakietu', sev: 'success' });
      loadPkgs();
    } catch (e) { setDialogErr(err(e, 'Nie udało się odnotować wykorzystania.')); }
  };

  const remainingFor = (cp: ClientPackage, serviceId: string) => {
    const item = cp.package.items.find((i) => i.serviceId === serviceId);
    if (!item) return 0;
    return item.quantity - cp.usages.filter((u) => u.serviceId === serviceId).length;
  };
  const usageLabel = (cp: ClientPackage) => {
    const total = cp.package.items.reduce((s, i) => s + i.quantity, 0);
    return `${cp.usages.length}/${total}`;
  };

  const cancelSoldPkg = async (cp: ClientPackage) => {
    const total = cp.package.items.reduce((s, i) => s + i.quantity, 0);
    const left = Math.max(0, total - cp.usages.length);
    const ok = await confirm({
      title: 'Anulować pakiet klienta?',
      details: (
        <>
          <strong>{cp.package.name}</strong> · {cp.client.firstName} {cp.client.lastName}
          <br />
          Wykorzystano {cp.usages.length} z {countLabel(total, 'usługi', 'usług', 'usług')}
        </>
      ),
      description: left > 0
        ? `Niewykorzystane usługi (${formatNumber(left)}) przepadną, a pakietu nie będzie można już użyć. Tej operacji nie można cofnąć.`
        : 'Pakietu nie będzie można już użyć. Tej operacji nie można cofnąć.',
      confirmLabel: 'Anuluj pakiet',
      tone: 'danger',
    });
    if (!ok) return;
    try { await packageApi.cancelSold(cp.id); loadPkgs(); } catch (e) { setToast({ msg: err(e, 'Nie udało się anulować pakietu. Spróbuj ponownie.'), sev: 'error' }); }
  };

  const openCardDialog = () => { setDialogErr(''); setCardDialog(true); };
  const openPkgDialog = () => { setDialogErr(''); setPkgDialog(true); };
  const cardFiltered = Boolean(cardSearch || cardStatus);

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Marketing' }]}
        title="Karty podarunkowe i pakiety"
        description="Sprzedawaj karty podarunkowe i pakiety usług, a potem rozliczaj je przy wizytach."
        actions={tab === 0 ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCardDialog}>Dodaj kartę</Button>
        ) : (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openPkgDialog}>Nowy pakiet</Button>
        )}
      >
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ borderBottom: `1px solid ${tokens.line}` }}>
          <Tab icon={<CardGiftcardIcon fontSize="small" />} iconPosition="start" label="Karty podarunkowe" />
          <Tab icon={<Inventory2OutlinedIcon fontSize="small" />} iconPosition="start" label="Pakiety usług" />
        </Tabs>
      </PageHeader>

      {/* ── KARTY PODARUNKOWE ── */}
      {tab === 0 && (
        <Stack gap={2.5}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' } }}>
            <StatTile label="Aktywne karty" value={formatNumber(stats.activeCards)} loading={cardsLoading && cards.length === 0} />
            <StatTile label="Saldo do wykorzystania" value={formatNumber(stats.cardBalance)} unit="zł" hint="na aktywnych kartach" loading={cardsLoading && cards.length === 0} />
            <StatTile label="Wartość sprzedanych kart" value={formatNumber(stats.cardSold)} unit="zł" loading={cardsLoading && cards.length === 0} sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }} />
          </Box>

          <SectionCard
            title="Karty podarunkowe"
            subtitle={cardFiltered ? 'Liczby powyżej uwzględniają wybrane filtry' : undefined}
            padding="none"
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} sx={{ px: { xs: 2, md: 2.5 }, py: 1.75, borderBottom: `1px solid ${tokens.line}` }}>
              <TextField
                size="small"
                placeholder="Szukaj po kodzie lub kliencie"
                value={cardSearch}
                onChange={(e) => setCardSearch(e.target.value)}
                inputProps={{ 'aria-label': 'Szukaj karty' }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: tokens.muted }} /></InputAdornment> }}
                sx={{ flex: 1, minWidth: 0, maxWidth: { sm: 360 } }}
              />
              <TextField
                select
                size="small"
                value={cardStatus}
                onChange={(e) => setCardStatus(e.target.value)}
                inputProps={{ 'aria-label': 'Status karty' }}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: { sm: 180 } }}
              >
                <MenuItem value="">Wszystkie statusy</MenuItem>
                {Object.entries(CARD_STATUS).map(([v, s]) => <MenuItem key={v} value={v}>{s.label}</MenuItem>)}
              </TextField>
            </Stack>

            {cardsLoading && cards.length === 0 ? (
              <Box sx={{ p: 2 }}><LoadingState variant="block" height={160} /></Box>
            ) : cards.length === 0 ? (
              cardFiltered ? (
                <EmptyState
                  compact
                  icon={<SearchIcon />}
                  title="Brak kart dla wybranych filtrów"
                  description="Zmień frazę lub status, aby zobaczyć inne karty."
                  actions={<Button variant="outlined" onClick={() => { setCardSearch(''); setCardStatus(''); }}>Wyczyść filtry</Button>}
                />
              ) : (
                <EmptyState
                  icon={<CardGiftcardIcon />}
                  title="Nie masz jeszcze kart podarunkowych"
                  description="Dodaj pierwszą kartę — kod wygenerujemy automatycznie, a saldo rozliczysz przy kolejnych wizytach."
                  actions={<Button variant="outlined" startIcon={<AddIcon />} onClick={openCardDialog}>Dodaj kartę</Button>}
                />
              )
            ) : (
              <TableContainer sx={{ opacity: cardsLoading ? 0.6 : 1, transition: 'opacity .15s ease' }}>
                <Table sx={{ minWidth: 820 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Kod</TableCell>
                      <TableCell>Klient</TableCell>
                      <TableCell align="right">Wartość</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                      <TableCell>Ważna do</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Akcje</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cards.map((c) => {
                      const st = CARD_STATUS[c.status] || { label: c.status, tone: 'neutral' as PillTone };
                      return (
                        <TableRow key={c.id} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.02em' }}>{c.code}</TableCell>
                          <TableCell>{c.client ? `${c.client.firstName} ${c.client.lastName}` : <Box component="span" sx={{ color: tokens.subtle }}>Na okaziciela</Box>}</TableCell>
                          <TableCell align="right" sx={tabular}>{zl(c.initialValue)}</TableCell>
                          <TableCell align="right" sx={{ ...tabular, fontWeight: 700, color: Number(c.balance) > 0 ? 'success.main' : 'text.secondary' }}>{zl(c.balance)}</TableCell>
                          <TableCell sx={tabular}>{c.expiresAt ? dt(c.expiresAt) : <Box component="span" sx={{ color: tokens.subtle }}>bezterminowo</Box>}</TableCell>
                          <TableCell><StatusPill label={st.label} tone={st.tone} /></TableCell>
                          <TableCell align="right">
                            <Stack direction="row" gap={0.5} justifyContent="flex-end" alignItems="center">
                              {c.status === 'ACTIVE' && (
                                <Button size="small" variant="outlined" onClick={() => { setDialogErr(''); setRedeemForm({ amount: '', note: '' }); setRedeemCard(c); }}>
                                  Zrealizuj
                                </Button>
                              )}
                              {(c.status === 'ACTIVE' || c.status === 'EXPIRED') && (
                                <Tooltip title="Anuluj kartę">
                                  <IconButton size="small" color="error" aria-label={`Anuluj kartę ${c.code}`} onClick={() => cancelCard(c)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>
        </Stack>
      )}

      {/* ── PAKIETY ── */}
      {tab === 1 && (
        <Stack gap={2.5}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' } }}>
            <StatTile label="Pakiety w ofercie" value={formatNumber(stats.activePkgs)} hint={`z ${formatNumber(pkgs.length)} zdefiniowanych`} loading={pkgLoading && pkgs.length === 0} />
            <StatTile label="Aktywne pakiety klientów" value={formatNumber(stats.soldActive)} loading={pkgLoading && pkgs.length === 0} />
            <StatTile label="Sprzedane pakiety" value={formatNumber(sold.length)} hint="łącznie" loading={pkgLoading && pkgs.length === 0} sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }} />
          </Box>

          <Box>
            <Typography component="h2" variant="h4" sx={{ mb: 1.5 }}>Definicje pakietów</Typography>
            {pkgLoading && pkgs.length === 0 ? <LoadingState variant="cards" count={3} /> : pkgs.length === 0 ? (
              <SectionCard>
                <EmptyState
                  icon={<Inventory2OutlinedIcon />}
                  title="Nie masz jeszcze pakietów usług"
                  description="Połącz kilka usług w pakiet z niższą ceną — np. 5 masaży w cenie 4."
                  actions={<Button variant="outlined" startIcon={<AddIcon />} onClick={openPkgDialog}>Nowy pakiet</Button>}
                />
              </SectionCard>
            ) : (
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
                {pkgs.map((p) => {
                  const discount = p.originalPrice ? Math.round((1 - Number(p.price) / Number(p.originalPrice)) * 100) : 0;
                  return (
                    <Paper key={p.id} sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: p.isActive ? tokens.paper : tokens.surface }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.0625rem', color: p.isActive ? tokens.ink : tokens.muted }}>{p.name}</Typography>
                          {p.description && <Typography variant="body2" sx={{ color: tokens.muted, mt: 0.25 }}>{p.description}</Typography>}
                        </Box>
                        <StatusPill label={p.isActive ? 'W ofercie' : 'Wyłączony'} tone={p.isActive ? 'success' : 'neutral'} size="small" />
                      </Stack>
                      <Stack direction="row" gap={0.75} flexWrap="wrap">
                        {p.items.map((i) => (
                          <StatusPill key={i.id} noDot tone="plum" size="small" label={`${i.service.name}${i.quantity > 1 ? ` ×${i.quantity}` : ''}`} />
                        ))}
                      </Stack>
                      <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
                        <Typography sx={{ fontWeight: 750, fontSize: '1.375rem', ...tabular }}>{zl(p.price)}</Typography>
                        {p.originalPrice && (
                          <>
                            <Typography sx={{ fontSize: '0.9375rem', color: tokens.muted, textDecoration: 'line-through', ...tabular }}>{zl(p.originalPrice)}</Typography>
                            {discount > 0 && <StatusPill label={`−${discount}%`} tone="success" size="small" noDot />}
                          </>
                        )}
                      </Stack>
                      <Typography variant="body2" sx={{ color: tokens.muted }}>
                        {p.validityDays ? `Ważny ${countLabel(p.validityDays, 'dzień', 'dni', 'dni')} od zakupu` : 'Bez limitu ważności'}
                        {' · '}sprzedano: {formatNumber(p._count?.sold ?? 0)}
                      </Typography>
                      <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mt: 'auto', pt: 0.5 }}>
                        <Button size="small" variant="outlined" disabled={!p.isActive}
                          onClick={() => { setDialogErr(''); setSellForm({ clientId: '', pricePaid: '' }); setSellPkg(p); }}>
                          Sprzedaj
                        </Button>
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={<Switch size="small" checked={p.isActive} onChange={() => togglePkg(p)} />}
                          label={<Typography variant="body2">{p.isActive ? 'Aktywny' : 'Nieaktywny'}</Typography>}
                        />
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>

          <SectionCard title="Pakiety klientów" subtitle="Sprzedane pakiety i ich wykorzystanie" padding="none">
            {sold.length === 0 ? (
              <EmptyState
                compact
                icon={<Inventory2OutlinedIcon />}
                title="Brak sprzedanych pakietów"
                description="Użyj przycisku „Sprzedaj” przy definicji pakietu, aby przypisać go klientowi."
              />
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 860 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Klient</TableCell>
                      <TableCell>Pakiet</TableCell>
                      <TableCell>Kupiono</TableCell>
                      <TableCell>Wygasa</TableCell>
                      <TableCell>Wykorzystanie</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Akcje</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sold.map((cp) => {
                      const st = PKG_STATUS[cp.status] || { label: cp.status, tone: 'neutral' as PillTone };
                      return (
                        <TableRow key={cp.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{cp.client.firstName} {cp.client.lastName}</TableCell>
                          <TableCell>{cp.package.name}</TableCell>
                          <TableCell sx={tabular}>{dt(cp.purchasedAt)}</TableCell>
                          <TableCell sx={tabular}>{cp.expiresAt ? dt(cp.expiresAt) : <Box component="span" sx={{ color: tokens.subtle }}>bezterminowo</Box>}</TableCell>
                          <TableCell>
                            <Tooltip title={cp.package.items.map((i) => `${i.service.name}: pozostało ${remainingFor(cp, i.serviceId)}/${i.quantity}`).join(' · ')}>
                              <Box component="span" sx={{ ...tabular, fontWeight: 700, borderBottom: `1px dashed ${tokens.lineStrong}`, cursor: 'help' }}>{usageLabel(cp)}</Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell><StatusPill label={st.label} tone={st.tone} /></TableCell>
                          <TableCell align="right">
                            <Stack direction="row" gap={0.5} justifyContent="flex-end" alignItems="center">
                              {cp.status === 'ACTIVE' && (
                                <Button size="small" variant="outlined" onClick={() => { setDialogErr(''); setUseServiceId(''); setUsePkg(cp); }}>
                                  Użyj
                                </Button>
                              )}
                              {cp.status === 'ACTIVE' && (
                                <Tooltip title="Anuluj pakiet klienta">
                                  <IconButton size="small" color="error" aria-label={`Anuluj pakiet ${cp.package.name}`} onClick={() => cancelSoldPkg(cp)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>
        </Stack>
      )}

      {/* ── DIALOG: nowa karta ── */}
      <Dialog open={cardDialog} onClose={() => !cardSaving && setCardDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nowa karta podarunkowa</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 0.5 }}>
            {dialogErr && <Alert severity="error" onClose={() => setDialogErr('')}>{dialogErr}</Alert>}
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' }}>
              <TextField type="number" label="Wartość karty" fullWidth required
                inputProps={{ min: 0, step: 10, inputMode: 'decimal' }}
                InputProps={unit('zł')}
                value={cardForm.value} onChange={(e) => setCardForm({ ...cardForm, value: e.target.value })} />
              <TextField type="date" label="Ważna do" fullWidth InputLabelProps={{ shrink: true }}
                helperText="Puste — karta bezterminowa"
                value={cardForm.expiresAt} onChange={(e) => setCardForm({ ...cardForm, expiresAt: e.target.value })} />
            </Box>
            <Autocomplete
              options={clients}
              getOptionLabel={(o) => `${o.firstName} ${o.lastName}`}
              value={clients.find((c) => c.id === cardForm.clientId) || null}
              onChange={(_, v) => setCardForm({ ...cardForm, clientId: v?.id || '' })}
              noOptionsText="Brak klientów"
              renderInput={(params) => <TextField {...params} label="Klient" helperText="Opcjonalnie — bez klienta karta jest na okaziciela" />}
            />
            <TextField label="Notatka" fullWidth multiline rows={2} placeholder="np. od kogo i dla kogo"
              value={cardForm.note} onChange={(e) => setCardForm({ ...cardForm, note: e.target.value })} />
            <Typography variant="body2" sx={{ color: tokens.muted }}>
              Kod karty zostanie wygenerowany automatycznie i pokazany po utworzeniu.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setCardDialog(false)} disabled={cardSaving}>Anuluj</Button>
          <Button variant="contained" onClick={createCard} disabled={cardSaving}
            startIcon={cardSaving ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Utwórz kartę
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOG: realizacja karty ── */}
      <Dialog open={!!redeemCard} onClose={() => setRedeemCard(null)} fullWidth maxWidth="xs">
        <DialogTitle>Zrealizuj kartę podarunkową</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 0.5 }}>
            {dialogErr && <Alert severity="error" onClose={() => setDialogErr('')}>{dialogErr}</Alert>}
            <Box sx={{ bgcolor: tokens.ground, borderRadius: '10px', px: 1.75, py: 1.5 }}>
              <Typography variant="body2" sx={{ color: tokens.muted }}>
                Karta <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: tokens.ink }}>{redeemCard?.code}</Box>
                {redeemCard?.client ? ` · ${redeemCard.client.firstName} ${redeemCard.client.lastName}` : ''}
              </Typography>
              <Typography sx={{ mt: 0.25 }}>
                Dostępne saldo: <Box component="strong" sx={tabular}>{zl(redeemCard?.balance)}</Box>
              </Typography>
            </Box>
            <TextField type="number" label="Kwota do realizacji" fullWidth required
              inputProps={{ min: 0, step: 1, inputMode: 'decimal' }}
              InputProps={unit('zł')}
              helperText={redeemCard ? `Maksymalnie ${zl(redeemCard.balance)}` : undefined}
              value={redeemForm.amount} onChange={(e) => setRedeemForm({ ...redeemForm, amount: e.target.value })} />
            <TextField label="Notatka" fullWidth multiline rows={2} placeholder="np. za jaką usługę"
              value={redeemForm.note} onChange={(e) => setRedeemForm({ ...redeemForm, note: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setRedeemCard(null)}>Anuluj</Button>
          <Button variant="contained" onClick={doRedeem}>Zrealizuj płatność</Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOG: nowy pakiet ── */}
      <Dialog open={pkgDialog} onClose={() => !pkgSaving && setPkgDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nowy pakiet usług</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 0.5 }}>
            {dialogErr && <Alert severity="error" onClose={() => setDialogErr('')}>{dialogErr}</Alert>}
            <TextField label="Nazwa pakietu" fullWidth required placeholder="np. 5 masaży relaksacyjnych"
              value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} />
            <TextField label="Opis" fullWidth multiline rows={2} helperText="Opcjonalnie"
              value={pkgForm.description} onChange={(e) => setPkgForm({ ...pkgForm, description: e.target.value })} />
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' }, alignItems: 'start' }}>
              <TextField type="number" label="Cena pakietu" fullWidth required
                inputProps={{ min: 0, inputMode: 'decimal' }} InputProps={unit('zł')}
                value={pkgForm.price} onChange={(e) => setPkgForm({ ...pkgForm, price: e.target.value })} />
              <TextField type="number" label="Cena regularna" fullWidth
                inputProps={{ min: 0, inputMode: 'decimal' }} InputProps={unit('zł')}
                helperText="Pokażemy rabat"
                value={pkgForm.originalPrice} onChange={(e) => setPkgForm({ ...pkgForm, originalPrice: e.target.value })} />
              <TextField type="number" label="Ważność" fullWidth
                inputProps={{ min: 1, inputMode: 'numeric' }} InputProps={unit('dni')}
                helperText="Puste — bez limitu"
                value={pkgForm.validityDays} onChange={(e) => setPkgForm({ ...pkgForm, validityDays: e.target.value })} />
            </Box>
            <Box>
              <Typography component="h3" variant="h6" sx={{ mb: 0.25 }}>Usługi w pakiecie</Typography>
              <Typography variant="body2" sx={{ color: tokens.muted, mb: 1.5 }}>Wybierz usługi i podaj, ile razy klient może z nich skorzystać.</Typography>
              <Stack spacing={1.5}>
                {pkgItems.map((item, idx) => (
                  <Stack key={idx} direction="row" gap={1} alignItems="flex-end">
                    <TextField select fullWidth label={idx === 0 ? 'Usługa' : undefined} value={item.serviceId}
                      inputProps={{ 'aria-label': `Usługa ${idx + 1}` }}
                      SelectProps={{ displayEmpty: true }}
                      sx={{ flex: 1, minWidth: 0 }}
                      onChange={(e) => setPkgItems(pkgItems.map((it, i) => (i === idx ? { ...it, serviceId: e.target.value } : it)))}>
                      <MenuItem value="" disabled>Wybierz usługę</MenuItem>
                      {services.map((s) => <MenuItem key={s.id} value={s.id}>{s.name} ({formatMoney(s.price)})</MenuItem>)}
                    </TextField>
                    <TextField type="number" label={idx === 0 ? 'Liczba' : undefined} sx={{ width: 96, flexShrink: 0 }}
                      inputProps={{ min: 1, inputMode: 'numeric', 'aria-label': `Liczba wizyt ${idx + 1}` }}
                      value={item.quantity}
                      onChange={(e) => setPkgItems(pkgItems.map((it, i) => (i === idx ? { ...it, quantity: Math.max(1, Number(e.target.value) || 1) } : it)))} />
                    <IconButton color="error" disabled={pkgItems.length === 1} aria-label="Usuń usługę z pakietu" sx={{ mb: 0.25 }}
                      onClick={() => setPkgItems(pkgItems.filter((_, i) => i !== idx))}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Button size="small" variant="text" startIcon={<AddIcon />} onClick={() => setPkgItems([...pkgItems, { serviceId: '', quantity: 1 }])} sx={{ mt: 1 }}>
                Dodaj usługę
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setPkgDialog(false)} disabled={pkgSaving}>Anuluj</Button>
          <Button variant="contained" onClick={createPkg} disabled={pkgSaving}
            startIcon={pkgSaving ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Utwórz pakiet
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOG: sprzedaż pakietu ── */}
      <Dialog open={!!sellPkg} onClose={() => setSellPkg(null)} fullWidth maxWidth="xs">
        <DialogTitle>Sprzedaj pakiet „{sellPkg?.name}”</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 0.5 }}>
            {dialogErr && <Alert severity="error" onClose={() => setDialogErr('')}>{dialogErr}</Alert>}
            <Autocomplete
              options={clients}
              getOptionLabel={(o) => `${o.firstName} ${o.lastName}`}
              value={clients.find((c) => c.id === sellForm.clientId) || null}
              onChange={(_, v) => setSellForm({ ...sellForm, clientId: v?.id || '' })}
              noOptionsText="Brak klientów"
              renderInput={(params) => <TextField {...params} label="Klient" required />}
            />
            <TextField type="number" fullWidth
              label="Cena sprzedaży"
              inputProps={{ min: 0, inputMode: 'decimal' }}
              InputProps={unit('zł')}
              placeholder={sellPkg ? formatNumber(sellPkg.price) : undefined}
              helperText={`Puste — cena pakietu (${zl(sellPkg?.price)})`}
              value={sellForm.pricePaid} onChange={(e) => setSellForm({ ...sellForm, pricePaid: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setSellPkg(null)}>Anuluj</Button>
          <Button variant="contained" onClick={doSell}>Sprzedaj pakiet</Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOG: wykorzystanie usługi z pakietu ── */}
      <Dialog open={!!usePkg} onClose={() => setUsePkg(null)} fullWidth maxWidth="xs">
        <DialogTitle>Odnotuj wykorzystanie pakietu</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 0.5 }}>
            {dialogErr && <Alert severity="error" onClose={() => setDialogErr('')}>{dialogErr}</Alert>}
            <Box sx={{ bgcolor: tokens.ground, borderRadius: '10px', px: 1.75, py: 1.5 }}>
              <Typography sx={{ fontWeight: 700 }}>{usePkg?.package.name}</Typography>
              <Typography variant="body2" sx={{ color: tokens.muted }}>
                Klient: {usePkg?.client.firstName} {usePkg?.client.lastName}
              </Typography>
            </Box>
            <TextField select fullWidth label="Usługa" value={useServiceId} onChange={(e) => setUseServiceId(e.target.value)}
              SelectProps={{ displayEmpty: true }}>
              <MenuItem value="" disabled>Wybierz usługę</MenuItem>
              {usePkg?.package.items.map((i) => {
                const left = usePkg ? remainingFor(usePkg, i.serviceId) : 0;
                return (
                  <MenuItem key={i.id} value={i.serviceId} disabled={left <= 0}>
                    {i.service.name} — pozostało {left}/{i.quantity}
                  </MenuItem>
                );
              })}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setUsePkg(null)}>Anuluj</Button>
          <Button variant="contained" onClick={doUse}>Odnotuj wykorzystanie</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert variant="filled" severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};
